import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "./supabaseClient";
import EventItemsEditor from "./EventItemsEditor";

// ─── Shared store for all event_items belonging to one event ────────────────
// Wraps EventReviewPage. Children components use the slot components below
// (or useScopeLoadout directly) to read/write items for a specific scope.

const Ctx = createContext(null);

export function EventLoadoutProvider({ eventId, canEdit = true, children }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mirror state into a ref so the debounced flush always sees the latest array.
  // Without this, flush captures whatever `rows` was at the time emitScopeChange
  // first ran — which silently delete-syncs every later edit because the snapshot
  // doesn't include the new items.
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Load on mount / when eventId changes
  const loadRef = useRef(0);
  const load = useCallback(async () => {
    if (!eventId) return;
    const my = ++loadRef.current;
    setLoading(true);
    const { data } = await supabase
      .from("event_items")
      .select(`
        id, event_id, group_name, event_group_ship_id, flagship_id, attendee_user_id,
        item_id, quantity, note, required, sort_order, instance_index, container_size_scu,
        item:sc_items!event_items_item_id_fkey(id, name, image_url, category, subcategory, manufacturer, item_group)
      `)
      .eq("event_id", eventId)
      .order("sort_order", { ascending: true });
    if (my !== loadRef.current) return;
    setRows((data || []).map(r => ({ ...r, _item: r.item || null })));
    setLoading(false);
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  // ── Save: debounced diff against DB per scope ─────────────────────────────
  const saveTimer = useRef(null);
  const pendingScopes = useRef(new Map()); // key → scope obj

  function scopeKey(scope) {
    if (scope.attendeeUserId) return `attendee:${scope.attendeeUserId}`;
    if (scope.flagshipId) return `flagship:${scope.flagshipId}`;
    if (scope.groupShipId) return `groupShip:${scope.groupShipId}`;
    if (scope.groupName) return `group:${scope.groupName}`;
    return "event";
  }
  function sameScope(r, scope) {
    return (r.attendee_user_id || null) === (scope.attendeeUserId || null)
        && (r.flagship_id || null) === (scope.flagshipId || null)
        && (r.event_group_ship_id || null) === (scope.groupShipId || null)
        && (r.group_name || null) === (scope.groupName || null);
  }
  function stripClient(r) {
    const { _item, item, temp_id, _editingNote, ...rest } = r;
    return rest;
  }

  // flush is held in a ref so emitScopeChange's setTimeout can call the LATEST
  // flush (which reads from rowsRef) regardless of which render scheduled it.
  const flushRef = useRef(() => {});
  flushRef.current = async function flush() {
    const scopes = [...pendingScopes.current.values()];
    pendingScopes.current.clear();
    // Read the CURRENT rows via ref so a stale closure doesn't silently delete data.
    const snapshot = rowsRef.current;
    for (const scope of scopes) {
      const local = snapshot.filter(r => sameScope(r, scope));

      const base = supabase.from("event_items").select("id").eq("event_id", eventId);
      const filtered = scope.attendeeUserId
        ? base.eq("attendee_user_id", scope.attendeeUserId)
        : scope.groupShipId
          ? base.eq("event_group_ship_id", scope.groupShipId)
          : scope.flagshipId
            ? base.eq("flagship_id", scope.flagshipId)
            : scope.groupName
              ? base.is("flagship_id", null).is("event_group_ship_id", null).is("attendee_user_id", null).eq("group_name", scope.groupName)
              : base.is("flagship_id", null).is("event_group_ship_id", null).is("group_name", null).is("attendee_user_id", null);
      const { data: dbRows, error: selErr } = await filtered;
      if (selErr) { console.error("[loadout] select failed", selErr); continue; }
      const dbIds = new Set((dbRows || []).map(r => r.id));
      const keep = new Set(local.filter(r => r.id).map(r => r.id));
      const toDelete = [...dbIds].filter(id => !keep.has(id));
      if (toDelete.length) {
        const { error: delErr } = await supabase.from("event_items").delete().in("id", toDelete);
        if (delErr) console.error("[loadout] delete failed", delErr);
      }

      const inserts = local.filter(r => !r.id).map(stripClient);
      const updates = local.filter(r => r.id).map(stripClient);

      if (inserts.length) {
        const { data: inserted, error: insErr } = await supabase.from("event_items").insert(inserts).select();
        if (insErr) console.error("[loadout] insert failed", insErr);
        if (inserted && inserted.length) {
          const tempIds = local.filter(r => !r.id).map(r => r.temp_id);
          setRows(prev => {
            const next = prev.slice();
            inserted.forEach((row, i) => {
              const tempId = tempIds[i];
              const idx = next.findIndex(r => r.temp_id === tempId);
              if (idx >= 0) next[idx] = { ...next[idx], id: row.id, temp_id: undefined };
            });
            return next;
          });
        }
      }
      for (const u of updates) {
        const { error: updErr } = await supabase.from("event_items").update(u).eq("id", u.id);
        if (updErr) console.error("[loadout] update failed", updErr);
      }
    }
  };

  const emitScopeChange = useCallback((scope, newItems) => {
    const stamped = newItems.map((it, i) => ({
      ...it,
      event_id: eventId,
      group_name: scope.groupName || null,
      event_group_ship_id: scope.groupShipId || null,
      flagship_id: scope.flagshipId || null,
      attendee_user_id: scope.attendeeUserId || null,
      instance_index: it.instance_index ?? null,
      container_size_scu: it.container_size_scu ?? null,
      sort_order: i,
    }));
    setRows(prev => {
      const others = prev.filter(r => !sameScope(r, scope));
      return [...others, ...stamped];
    });
    pendingScopes.current.set(scopeKey(scope), scope);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Indirection through the ref means we always call the latest flush.
    saveTimer.current = setTimeout(() => flushRef.current(), 700);
  }, [eventId]);

  // Make sure any pending save fires before unmount so navigating away doesn't lose changes.
  useEffect(() => {
    return () => {
      if (saveTimer.current && pendingScopes.current.size > 0) {
        clearTimeout(saveTimer.current);
        flushRef.current();
      }
    };
  }, []);

  const value = useMemo(() => ({
    rows, loading, canEdit, eventId, emitScopeChange,
  }), [rows, loading, canEdit, eventId, emitScopeChange]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEventLoadout() {
  return useContext(Ctx);
}

// Filter rows belonging to a scope.
export function useScopeRows(scope) {
  const ctx = useEventLoadout();
  return useMemo(() => {
    if (!ctx) return [];
    return ctx.rows.filter(r =>
      (r.attendee_user_id || null) === (scope.attendeeUserId || null)
      && (r.flagship_id || null) === (scope.flagshipId || null)
      && (r.event_group_ship_id || null) === (scope.groupShipId || null)
      && (r.group_name || null) === (scope.groupName || null)
    );
  }, [ctx, scope.attendeeUserId, scope.flagshipId, scope.groupShipId, scope.groupName]);
}

// ─── Slot components ────────────────────────────────────────────────────────
// FlagshipLoadoutSlot — embedded inside a flagship row in FlagshipSelector.
// Shows Ship Weapons & Components + Crew FPS Gear editors.
export function FlagshipLoadoutSlot({ flagshipId, accentColor = "#fcd657" }) {
  const ctx = useEventLoadout();
  const list = useScopeRows({ flagshipId });
  if (!ctx) return null;
  const weapons = list.filter(r => ["Ship Weapons", "Components"].includes(r._item?.item_group));
  const cargo = list.filter(r => r._item?.item_group === "Commodity");
  const crew = list.filter(r => r._item?.item_group === "Personal Equipment");
  const other = list.filter(r => r._item && !["Ship Weapons", "Components", "Personal Equipment", "Commodity"].includes(r._item.item_group));

  if (!ctx.canEdit) return <ReadOnlyList rows={list} />;
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${accentColor}22` }}>
      <EventItemsEditor
        items={weapons.concat(other)}
        onChange={(next) => ctx.emitScopeChange({ flagshipId }, [...next, ...cargo, ...crew])}
        title="Ship Weapons & Components"
        addLabel="Add Weapon/Component"
        groupFilter={["Ship Weapons", "Components"]}
        accentColor={accentColor}
        emptyHint="No weapons or components recommended"
        compact
      />
      <EventItemsEditor
        items={cargo}
        onChange={(next) => ctx.emitScopeChange({ flagshipId }, [...weapons, ...other, ...next, ...crew])}
        title="Cargo Grid Commodities"
        addLabel="Add Commodity"
        groupFilter={["Commodity"]}
        accentColor="#34d399"
        emptyHint="No cargo loadout yet"
        compact
      />
      <EventItemsEditor
        items={crew}
        onChange={(next) => ctx.emitScopeChange({ flagshipId }, [...weapons, ...other, ...cargo, ...next])}
        title="Crew FPS Gear"
        addLabel="Add FPS Gear"
        groupFilter={["Personal Equipment"]}
        accentColor="#a78bfa"
        emptyHint="No FPS gear recommended"
        compact
      />
    </div>
  );
}

// ShipLoadoutSlot — embedded inside a single ship row in GroupShipAssignments.
export function ShipLoadoutSlot({ groupShipId, accentColor = "#fcd657" }) {
  const ctx = useEventLoadout();
  const list = useScopeRows({ groupShipId });
  if (!ctx) return null;
  const weapons = list.filter(r => ["Ship Weapons", "Components"].includes(r._item?.item_group));
  const cargo = list.filter(r => r._item?.item_group === "Commodity");
  const crew = list.filter(r => r._item?.item_group === "Personal Equipment");

  if (!ctx.canEdit) return <ReadOnlyList rows={list} />;
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${accentColor}22` }}>
      <EventItemsEditor
        items={weapons}
        onChange={(next) => ctx.emitScopeChange({ groupShipId }, [...next, ...cargo, ...crew])}
        title="Ship Weapons & Components"
        addLabel="Add Weapon/Component"
        groupFilter={["Ship Weapons", "Components"]}
        accentColor={accentColor}
        emptyHint="No weapons or components recommended"
        compact
      />
      <EventItemsEditor
        items={cargo}
        onChange={(next) => ctx.emitScopeChange({ groupShipId }, [...weapons, ...next, ...crew])}
        title="Cargo Grid Commodities"
        addLabel="Add Commodity"
        groupFilter={["Commodity"]}
        accentColor="#34d399"
        emptyHint="No cargo loadout yet"
        compact
      />
      <EventItemsEditor
        items={crew}
        onChange={(next) => ctx.emitScopeChange({ groupShipId }, [...weapons, ...cargo, ...next])}
        title="Crew FPS Gear"
        addLabel="Add FPS Gear"
        groupFilter={["Personal Equipment"]}
        accentColor="#a78bfa"
        emptyHint="No FPS gear recommended"
        compact
      />
    </div>
  );
}

// GroupLoadoutSlot — embedded inside the Group Section (after ship requirements).
// Holds the group-level commodities & personal gear that aren't tied to one ship.
export function GroupLoadoutSlot({ groupName, accentColor = "#fcd657" }) {
  const ctx = useEventLoadout();
  const list = useScopeRows({ groupName });
  if (!ctx) return null;
  const commodities = list.filter(r => r._item?.item_group === "Personal Equipment" && ["Consumable", "Medical", "Utility Item"].includes(r._item?.category));
  const personalGear = list.filter(r => r._item?.item_group === "Personal Equipment" && !["Consumable", "Medical", "Utility Item"].includes(r._item?.category));
  const other = list.filter(r => r._item && r._item.item_group !== "Personal Equipment");

  if (!ctx.canEdit) return <ReadOnlyList rows={list} />;
  return (
    <div style={{ marginTop: 10 }}>
      <EventItemsEditor
        items={commodities}
        onChange={(next) => ctx.emitScopeChange({ groupName }, [...next, ...personalGear, ...other])}
        title="Group Commodities, Supplies & Medical"
        addLabel="Add Supply"
        groupFilter={["Personal Equipment"]}
        categoryFilter={["Consumable", "Medical", "Utility Item"]}
        accentColor="#34d399"
        emptyHint="No group commodities yet"
        compact
      />
      <EventItemsEditor
        items={personalGear}
        onChange={(next) => ctx.emitScopeChange({ groupName }, [...commodities, ...next, ...other])}
        title="Group Personal Gear (Weapons, Armor, Ammo)"
        addLabel="Add Personal Gear"
        groupFilter={["Personal Equipment"]}
        categoryFilter={["Weapon", "Armor", "Gadget", "Clothing"]}
        accentColor="#a78bfa"
        emptyHint="No group personal gear yet"
        compact
      />
    </div>
  );
}

// AttendeeLoadoutSlot — embedded inside an attendee row.
// Shows just the personal gear the organizer wants this specific attendee to bring.
export function AttendeeLoadoutSlot({ attendeeUserId, accentColor = "#a78bfa" }) {
  const ctx = useEventLoadout();
  const list = useScopeRows({ attendeeUserId });
  if (!ctx) return null;
  if (!ctx.canEdit) return <ReadOnlyList rows={list} />;
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${accentColor}22` }}>
      <EventItemsEditor
        items={list}
        onChange={(next) => ctx.emitScopeChange({ attendeeUserId }, next)}
        title="Personal Loadout"
        addLabel="Add Personal Gear"
        groupFilter={["Personal Equipment"]}
        accentColor={accentColor}
        emptyHint="No personal gear assigned"
        compact
      />
    </div>
  );
}

// EventWideLoadoutSlot — overall "everyone bring this" recommendations.
export function EventWideLoadoutSlot({ accentColor = "#fcd657" }) {
  const ctx = useEventLoadout();
  const list = useScopeRows({});
  if (!ctx) return null;
  if (!ctx.canEdit) return <ReadOnlyList rows={list} />;
  return (
    <EventItemsEditor
      items={list}
      onChange={(next) => ctx.emitScopeChange({}, next)}
      title="General Recommendations"
      addLabel="Add Item"
      accentColor={accentColor}
      emptyHint="No event-wide items yet"
      compact
    />
  );
}

// ─── Read-only list (viewer can't edit) ────────────────────────────────────
function ReadOnlyList({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
      {rows.map(r => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: r.required ? "rgba(239,83,80,0.06)" : "rgba(255,255,255,0.02)", border: `1px solid ${r.required ? "rgba(239,83,80,0.3)" : "rgba(255,255,255,0.05)"}` }}>
          {r._item?.image_url && <img src={r._item.image_url} alt={r._item.name} style={{ width: 32, height: 32, objectFit: "cover" }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r._item?.name || "(unknown)"}</div>
            {r.note && <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 11, color: "rgba(232,234,246,0.55)" }}>{r.note}</div>}
          </div>
          {r.required && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#ef5350", letterSpacing: 1.5, padding: "2px 6px", border: "1px solid #ef5350" }}>REQ</span>}
        </div>
      ))}
    </div>
  );
}
